/**
 * War Funds shop (Phase 6): permanent, stacking upgrades bought with the meta-currency
 * earned every battle. Pure config + math so it's testable and shared between the shop UI,
 * the reward loop, and the battle scene. Upgrades are PURELY ADDITIVE (no existing content
 * is gated behind them), so buying is always a gain.
 */
export type UpgradeId = "war_chest" | "fortify" | "foresight";

export type UpgradeDef = {
  id: UpgradeId;
  maxLevel: number;
  baseCost: number;
  /** Cost grows per level so later ranks are a real sink. */
  costGrowth: number;
};

export const UPGRADES: Record<UpgradeId, UpgradeDef> = {
  // +starting gold each battle.
  war_chest: { id: "war_chest", maxLevel: 5, baseCost: 200, costGrowth: 1.6 },
  // +castle HP (all segments) each battle.
  fortify: { id: "fortify", maxLevel: 5, baseCost: 250, costGrowth: 1.6 },
  // +1 free tech slot's worth of discount at the start (cheaper first techs).
  foresight: { id: "foresight", maxLevel: 3, baseCost: 300, costGrowth: 1.8 }
};

export const UPGRADE_IDS: UpgradeId[] = ["war_chest", "fortify", "foresight"];

/** Cost to buy the NEXT level (currentLevel -> currentLevel+1). Returns null if maxed. */
export function upgradeCost(id: UpgradeId, currentLevel: number): number | null {
  const def = UPGRADES[id];
  if (currentLevel >= def.maxLevel) return null;
  return Math.round(def.baseCost * Math.pow(def.costGrowth, currentLevel));
}

/** Starting-gold bonus granted by the war_chest level (added to EconomySystem.gold). */
export function startGoldBonus(level: number): number {
  return Math.max(0, Math.floor(level)) * 60;
}

/** Castle max-HP multiplier from the fortify level (e.g. 2 levels => 1.16x). */
export function fortressHpMultiplier(level: number): number {
  return 1 + Math.max(0, Math.floor(level)) * 0.08;
}

/** Per-tech cost reduction (fraction) from the foresight level, capped at 0.45. */
export function techDiscount(level: number): number {
  return Math.min(0.45, Math.max(0, Math.floor(level)) * 0.15);
}
